// =============================================================================
// ESPOLIDAR WEB ADMIN - USERS PAGE
// =============================================================================

import { useState } from 'react'
import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useToggleUserActive,
} from '../hooks/useUsers'
import { useResellers } from '../hooks/useResellers'
import {
  Users,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Building2,
  Shield,
  ShieldCheck,
  User as UserIcon,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import type { UserCreate, UserUpdate, UserDetail } from '../types'

const roleLabels: Record<string, { label: string; icon: typeof Shield; color: string }> = {
  superadmin: { label: 'Super Admin', icon: ShieldCheck, color: 'bg-red-100 text-red-700' },
  admin: { label: 'Admin', icon: Shield, color: 'bg-purple-100 text-purple-700' },
  user: { label: 'Utente', icon: UserIcon, color: 'bg-gray-100 text-gray-600' },
}

export default function UsersPage() {
  const { data: users, isLoading } = useUsers()
  const { data: resellers } = useResellers()
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()
  const toggleActive = useToggleUserActive()

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingUser, setEditingUser] = useState<UserDetail | null>(null)
  const [newUser, setNewUser] = useState<UserCreate>({
    username: '',
    email: '',
    password: '',
    role: 'user',
    reseller_id: null,
  })

  const handleDelete = (id: number, username: string) => {
    if (confirm(`Sei sicuro di voler eliminare l'utente "${username}"?`)) {
      deleteUser.mutate(id)
    }
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    createUser.mutate(newUser, {
      onSuccess: () => {
        setShowCreateForm(false)
        setNewUser({ username: '', email: '', password: '', role: 'user', reseller_id: null })
      },
    })
  }

  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    const data: UserUpdate = {
      username: editingUser.username,
      email: editingUser.email,
      role: editingUser.role,
      reseller_id: editingUser.reseller_id === null ? 0 : editingUser.reseller_id,
    }

    updateUser.mutate(
      { id: editingUser.id, data },
      {
        onSuccess: () => setEditingUser(null),
      }
    )
  }

  const handleToggleActive = (id: number) => {
    toggleActive.mutate(id)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Utenti</h1>
        <button
          onClick={() => setShowCreateForm(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          Nuovo Utente
        </button>
      </div>

      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Nuovo Utente</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username *
                </label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="input w-full"
                  required
                  minLength={3}
                  placeholder="username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="input w-full"
                  required
                  placeholder="email@esempio.it"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="input w-full"
                  required
                  minLength={6}
                  placeholder="Minimo 6 caratteri"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ruolo
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({
                      ...newUser,
                      role: e.target.value as 'superadmin' | 'admin' | 'user',
                      // Reset reseller se superadmin
                      reseller_id: e.target.value === 'superadmin' ? null : newUser.reseller_id,
                    })
                  }
                  className="input w-full"
                >
                  <option value="user">Utente</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>
              {newUser.role !== 'superadmin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reseller
                  </label>
                  <select
                    value={newUser.reseller_id || ''}
                    onChange={(e) =>
                      setNewUser({
                        ...newUser,
                        reseller_id: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    className="input w-full"
                  >
                    <option value="">-- Nessun reseller --</option>
                    {resellers?.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn btn-secondary"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createUser.isPending}
                >
                  {createUser.isPending ? 'Creazione...' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Modifica Utente</h2>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={editingUser.username}
                  onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                  className="input w-full"
                  required
                  minLength={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editingUser.email}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  className="input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ruolo
                </label>
                <select
                  value={editingUser.role}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      role: e.target.value as 'superadmin' | 'admin' | 'user',
                      reseller_id: e.target.value === 'superadmin' ? null : editingUser.reseller_id,
                    })
                  }
                  className="input w-full"
                >
                  <option value="user">Utente</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>
              {editingUser.role !== 'superadmin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reseller
                  </label>
                  <select
                    value={editingUser.reseller_id || ''}
                    onChange={(e) =>
                      setEditingUser({
                        ...editingUser,
                        reseller_id: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    className="input w-full"
                  >
                    <option value="">-- Nessun reseller --</option>
                    {resellers?.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name} ({r.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="btn btn-secondary"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updateUser.isPending}
                >
                  {updateUser.isPending ? 'Salvataggio...' : 'Salva'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {users && users.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-900">
                  Utente
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                  Ruolo
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                  Reseller
                </th>
                <th className="text-center px-6 py-4 text-sm font-semibold text-gray-900">
                  Stato
                </th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-900">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map((user) => {
                const roleInfo = roleLabels[user.role] || roleLabels.user
                const RoleIcon = roleInfo.icon
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.username}</p>
                          <p className="text-sm text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}
                      >
                        <RoleIcon size={12} />
                        {roleInfo.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {user.reseller_name ? (
                        <div className="flex items-center justify-center gap-1">
                          <Building2 size={14} className="text-gray-400" />
                          <span className="text-sm text-gray-700">{user.reseller_name}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleToggleActive(user.id)}
                        disabled={toggleActive.isPending}
                        className="cursor-pointer"
                        title={user.is_active ? 'Clicca per disattivare' : 'Clicca per attivare'}
                      >
                        {user.is_active ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle size={12} />
                            Attivo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <XCircle size={12} />
                            Disattivo
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Modifica"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(user.id, user.username)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          disabled={deleteUser.isPending}
                          title="Elimina"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun utente</h3>
          <p className="text-gray-500 mb-6">
            Crea il primo utente per iniziare.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus size={18} />
            Crea Utente
          </button>
        </div>
      )}
    </div>
  )
}
